/**
 * UsersList – Admin user table with client-side search, filter, sort (no composite indexes).
 * Fetches full list once on mount (up to 2000); filtering/sorting is local.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { ArrowLeft, List, ChevronUp, ChevronDown, Search } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import adminApi from '../../../api/accounts/adminApi';
import { formatDate } from '../../../utils/dates/dateUtils';

const PAGE_SIZE = 100;
const PLAN_OPTIONS = ['', 'Basic', 'Premium'];
const STATUS_OPTIONS = ['', 'active', 'trialing', 'past_due', 'canceled', 'inactive'];

function matchSearch(user, searchTrim) {
  if (!searchTrim) return true;
  const q = searchTrim.toLowerCase();
  const email = (user.email || '').toLowerCase();
  const name = (user.displayName || '').toLowerCase();
  return email.includes(q) || name.includes(q);
}

function ColumnHeader({ label, sortKey, getSortIcon, handleSort }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      <button type="button" onClick={() => handleSort(sortKey)} className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
        {label} {getSortIcon(sortKey)}
      </button>
    </th>
  );
}

export default function UsersList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const isAdmin = isUserAdmin(user);

  const [allUsers, setAllUsers] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [searchInput, setSearchInput] = useState('');
  const [planLevel, setPlanLevel] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');

  const searchTrim = (searchInput || '').trim();

  const filteredUsers = useMemo(() => {
    let list = allUsers;
    if (searchTrim) {
      list = list.filter((u) => matchSearch(u, searchTrim));
    }
    if (planLevel) {
      list = list.filter((u) => (u.planLevel || '').toLowerCase() === planLevel.toLowerCase());
    }
    if (subscriptionStatus) {
      list = list.filter(
        (u) => (String(u.subscriptionStatus || '').toLowerCase()) === subscriptionStatus.toLowerCase()
      );
    }
    const dir = order === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va = a[sort];
      let vb = b[sort];
      if (sort === 'createdAt' || sort === 'lastActive') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return (va - vb) * dir;
      }
      va = String(va ?? '').toLowerCase();
      vb = String(vb ?? '').toLowerCase();
      return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
    });
    return list;
  }, [allUsers, searchTrim, planLevel, subscriptionStatus, sort, order]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
        return;
      }
      setIsLoading(false);
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setInitialLoad(true);
    setError(null);
    adminApi
      .listUsers()
      .then((res) => {
        if (!cancelled) {
          setAllUsers(res.users || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load users');
          setAllUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setInitialLoad(false);
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const handleSort = (field) => {
    console.log('handleSort', field);
    if (sort === field) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else setSort(field);
    setCurrentPage(1);
  };

  const setPlan = (value) => {
    setPlanLevel(value);
    setCurrentPage(1);
  };
  const setStatus = (value) => {
    setSubscriptionStatus(value);
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sort !== key) return null;
    return order === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  const formatCellDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return isNaN(d) ? iso : formatDate(d);
    } catch {
      return iso;
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }
  if (!isAdmin) return null;

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      <div className="mb-6">
        <button
          onClick={() => navigate('/app/private/admin/users')}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg p-1"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          <span className="text-sm font-medium">Back to Users</span>
        </button>
        <div className="flex items-center mb-2">
          <List className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Users List</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Browse and search all users. Filters and sort run locally (no extra requests). Up to 2,000 users loaded once.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search email or name..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <select
          value={planLevel}
          onChange={(e) => setPlan(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          {PLAN_OPTIONS.map((o) => (
            <option key={o || 'all'} value={o}>{o || 'All plans'}</option>
          ))}
        </select>
        <select
          value={subscriptionStatus}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o || 'all'} value={o}>{o || 'All statuses'}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-transparent">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800/95 shadow-sm">
              <tr>
                <ColumnHeader label="UID" sortKey="uid" getSortIcon={getSortIcon} handleSort={handleSort} />
                <ColumnHeader label="Email" sortKey="email" getSortIcon={getSortIcon} handleSort={handleSort} />
                <ColumnHeader label="Name" sortKey="displayName" getSortIcon={getSortIcon} handleSort={handleSort} />
                <ColumnHeader label="Plan" sortKey="planLevel" getSortIcon={getSortIcon} handleSort={handleSort} />
                <ColumnHeader label="Status" sortKey="subscriptionStatus" getSortIcon={getSortIcon} handleSort={handleSort} />
                <ColumnHeader label="Created" sortKey="createdAt" getSortIcon={getSortIcon} handleSort={handleSort} />
                <ColumnHeader label="Last Active" sortKey="lastActive" getSortIcon={getSortIcon} handleSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {initialLoad && allUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No users match the current filters.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((u) => (
                  <tr key={u.uid} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 font-mono truncate max-w-[120px]" title={u.uid}>
                      {u.uid?.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{u.email || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{u.displayName || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{u.planLevel || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{u.subscriptionStatus || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{formatCellDate(u.createdAt)}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{formatCellDate(u.lastActive)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2 bg-gray-50 dark:bg-gray-800/50">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {currentPage} of {totalPages} · {filteredUsers.length.toLocaleString()} shown
            {allUsers.length > 0 && ` (${allUsers.length} loaded)`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
